import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Stack,
    Typography,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn } from "@lib/apis";
import { useEffect, useState } from "react";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import IconButton from "@mui/material/IconButton";
import { AddCircleOutline, Delete, Edit, ArrowBack, Assessment, ListAlt } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useNavigate, useSearchParams } from "react-router-dom";
import { urls } from "@routes";
import { Survey, SurveyQuestion } from "@admin/types/survey.types";

interface PageData {
    status_code: number;
    status_text: string;
    questions: SurveyQuestion[];
    total_count: number;
}

interface SurveysData {
    status_code: number;
    status_text: string;
    surveys: Survey[];
    total_count: number;
}

// Question type color mapping
const questionTypeColors: Record<string, "primary" | "secondary" | "success" | "warning" | "error" | "info"> = {
    dropdown: "primary",
    radio: "secondary",
    checkbox: "success",
    scale: "warning",
    text: "info",
    textarea: "info",
    attachment: "error",
};

export default function AdminSurveyQuestions() {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [pageData, setPageData] = useState<PageData>();
    const [surveysData, setSurveysData] = useState<SurveysData>();
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [actionsDisabled, setActionsDisabled] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [questionToDelete, setQuestionToDelete] = useState<string | null>(null);
    const [selectedSurveyUid, setSelectedSurveyUid] = useState<string>(searchParams.get("survey_uid") || "");

    // Query for fetching all surveys
    const surveysQuery = useQuery({
        queryKey: ["surveysForFilter"],
        queryFn: () => authenticateAndFetchData("/admin/surveys/"),
        gcTime: 0,
        retry: retryFn,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (surveysQuery.data) {
            setSurveysData(surveysQuery.data.data);
        }
    }, [surveysQuery.data]);

    // Query for fetching survey questions
    const pageDataQuery = useQuery({
        queryKey: ["surveyQuestionsPageData", selectedSurveyUid],
        queryFn: () => {
            let url = "/admin/survey/questions/";
            if (selectedSurveyUid) {
                url += `?survey_uid=${selectedSurveyUid}`;
            }
            return authenticateAndFetchData(url);
        },
        gcTime: 0,
        retry: retryFn,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (pageDataQuery.data) {
            setPageData(pageDataQuery.data.data);
        }
    }, [pageDataQuery.data]);

    // Mutation for deleting a single question
    const deleteQuestionMutation = useMutation({
        mutationKey: ["deleteSurveyQuestion"],
        mutationFn: (uid: string) => authenticateAndPostData("/admin/survey/questions/delete/", {
            question_uid: uid,
        }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: () => {
            pageDataQuery.refetch().then();
            enqueueSnackbar("Question deleted successfully", { variant: "success" });
            setDeleteDialogOpen(false);
            setQuestionToDelete(null);
        },
        onError: (error: ApiRequestFailed) => {
            console.error(error);
            enqueueSnackbar(error.data.message || "Failed to delete question", {
                variant: "error",
            });
        }
    });

    // Mutation for bulk deleting questions
    const bulkDeleteMutation = useMutation({
        mutationKey: ["bulkDeleteSurveyQuestions"],
        mutationFn: (uids: string[]) => authenticateAndPostData("/admin/survey/questions/bulk-delete/", {
            question_uids: uids,
        }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: () => {
            pageDataQuery.refetch().then();
            setSelectedRows([]);
            enqueueSnackbar("Questions deleted successfully", { variant: "success" });
        },
        onError: (error: ApiRequestFailed) => {
            console.error(error);
            enqueueSnackbar(error.data.message || "Failed to delete questions", {
                variant: "error",
            });
        }
    });

    // Enable/disable actions based on selection and loading states
    useEffect(() => {
        setActionsDisabled(
            (selectedRows.length === 0) ||
            deleteQuestionMutation.isPending ||
            bulkDeleteMutation.isPending
        );
    }, [selectedRows, deleteQuestionMutation.isPending, bulkDeleteMutation.isPending]);

    const columns: GridColDef<SurveyQuestion>[] = [
        {
            field: "display_order",
            headerName: "Order",
            width: 70,
            hideable: false,
            editable: false,
            filterable: true,
            sortable: true,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Box display="flex" alignItems="center" justifyContent="center" gap={1} width="100%">
                    {params.value}
                </Box>
            )
        },
        {
            field: "survey_title",
            headerName: "Survey",
            width: 150,
            hideable: false,
            editable: false,
            filterable: true,
            sortable: true,
            align: "left",
            headerAlign: "center",
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    size="small"
                    color="primary"
                    variant="outlined"
                />
            )
        },
        {
            field: "question_text",
            headerName: "Question Text",
            flex: 1,
            minWidth: 250,
            hideable: false,
            editable: false,
            filterable: true,
            sortable: true,
            align: "left",
            headerAlign: "center",
            renderCell: (params) => (
                <Tooltip title={params.value}>
                    <Typography
                        variant="body2"
                        sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            width: "100%",
                        }}
                    >
                        {params.value}
                    </Typography>
                </Tooltip>
            )
        },
        {
            field: "question_type",
            headerName: "Type",
            width: 100,
            hideable: false,
            editable: false,
            filterable: true,
            sortable: true,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    size="small"
                    color={questionTypeColors[params.value] || "default"}
                />
            )
        },
        {
            field: "is_active",
            headerName: "Status",
            width: 90,
            hideable: false,
            editable: false,
            filterable: true,
            sortable: true,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Chip
                    label={params.value ? "Active" : "Inactive"}
                    size="small"
                    color={params.value ? "success" : "error"}
                    variant="outlined"
                />
            )
        },
        {
            field: "is_required",
            headerName: "Required",
            width: 90,
            hideable: true,
            editable: false,
            filterable: true,
            sortable: true,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Chip
                    label={params.value ? "Yes" : "No"}
                    size="small"
                    variant="outlined"
                />
            )
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 100,
            filterable: false,
            sortable: false,
            hideable: false,
            editable: false,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => {
                return (
                    <Stack direction="row" spacing={0.5} justifyContent="center" width="100%">
                        <Tooltip title="Edit Question">
                            <IconButton
                                color="primary"
                                size="small"
                                onClick={() => navigate(urls["adminEditSurveyQuestion"].replace(":questionUid", params.row.uid))}
                            >
                                <Edit fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Question">
                            <IconButton
                                color="error"
                                size="small"
                                onClick={() => {
                                    setQuestionToDelete(params.row.uid);
                                    setDeleteDialogOpen(true);
                                }}
                            >
                                <Delete fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                );
            }
        }
    ];

    function handleDeleteSingle() {
        if (questionToDelete) {
            deleteQuestionMutation.mutate(questionToDelete);
        }
    }

    function handleBulkDelete() {
        if (selectedRows.length > 0) {
            bulkDeleteMutation.mutate(selectedRows);
        }
    }

    // Calculate quick stats
    const stats = pageData ? {
        total: pageData.total_count,
        active: pageData.questions.filter(q => q.is_active).length,
        inactive: pageData.questions.filter(q => !q.is_active).length,
        required: pageData.questions.filter(q => q.is_required).length,
    } : null;

    // Handle survey filter change
    const handleSurveyFilterChange = (surveyUid: string) => {
        setSelectedSurveyUid(surveyUid);
        if (surveyUid) {
            setSearchParams({ survey_uid: surveyUid });
        } else {
            setSearchParams({});
        }
    };

    // ============================================================
    // --------------------- MAIN RENDER CODE ---------------------
    // ============================================================

    if (pageDataQuery.isLoading || surveysQuery.isLoading) {
        return <PageLoading />;
    } else if (pageDataQuery.error as unknown as ApiRequestFailed) {
        return <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed} />;
    } else if (pageData && surveysData) {
        return (
            <Stack direction="column" spacing={3} sx={{ pb: 4 }}>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", sm: "center" }}
                    spacing={2}
                >
                    <Box>
                        <Typography fontWeight="bold" variant="h5">
                            Survey Questions Management
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Manage questions for each survey
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ArrowBack />}
                        onClick={() => navigate(urls["adminSurveys"])}
                        sx={{ minWidth: { xs: "100%", sm: "auto" } }}
                    >
                        Back to Surveys
                    </Button>
                </Stack>

                <Divider />

                {/* Survey Filter and Quick Stats */}
                <Stack direction="row" spacing={2} alignItems="center">
                    <FormControl sx={{ minWidth: { xs: "100%", sm: 300 } }}>
                        <InputLabel>Filter by Survey</InputLabel>
                        <Select
                            value={selectedSurveyUid}
                            onChange={(e) => handleSurveyFilterChange(e.target.value)}
                            label="Filter by Survey"
                        >
                            <MenuItem value="">
                                <em>All Surveys</em>
                            </MenuItem>
                            {surveysData.surveys.map((survey) => (
                                <MenuItem key={survey.uid} value={survey.uid}>
                                    {survey.title} {!survey.is_active && "(Inactive)"}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                {/* Quick Stats Cards */}
                {stats && (
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                        <Card sx={{ flexGrow: 1, flexBasis: 0, minWidth: "200px" }}>
                            <CardContent>
                                <Typography variant="body2" color="text.secondary">
                                    Total Questions
                                </Typography>
                                <Typography variant="h4" sx={{ mt: 1 }}>
                                    {stats.total}
                                </Typography>
                            </CardContent>
                        </Card>
                        <Card sx={{ flexGrow: 1, flexBasis: 0, minWidth: "200px" }}>
                            <CardContent>
                                <Typography variant="body2" color="text.secondary">
                                    Active Questions
                                </Typography>
                                <Typography variant="h4" sx={{ mt: 1 }} color="success.main">
                                    {stats.active}
                                </Typography>
                            </CardContent>
                        </Card>
                        <Card sx={{ flexGrow: 1, flexBasis: 0, minWidth: "200px" }}>
                            <CardContent>
                                <Typography variant="body2" color="text.secondary">
                                    Inactive Questions
                                </Typography>
                                <Typography variant="h4" sx={{ mt: 1 }} color="error.main">
                                    {stats.inactive}
                                </Typography>
                            </CardContent>
                        </Card>
                        <Card sx={{ flexGrow: 1, flexBasis: 0, minWidth: "200px" }}>
                            <CardContent>
                                <Typography variant="body2" color="text.secondary">
                                    Required Questions
                                </Typography>
                                <Typography variant="h4" sx={{ mt: 1 }}>
                                    {stats.required}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Stack>
                )}

                {/* Action Buttons */}
                <Box
                    display="flex"
                    flexDirection={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    gap={2}
                >
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ width: { xs: "100%", md: "auto" } }}>
                        <Button
                            variant="contained"
                            size="medium"
                            startIcon={<AddCircleOutline />}
                            onClick={() => {
                                const url = selectedSurveyUid
                                    ? `${urls["adminCreateSurveyQuestion"]}?survey_uid=${selectedSurveyUid}`
                                    : urls["adminCreateSurveyQuestion"];
                                navigate(url);
                            }}
                            sx={{
                                flex: { xs: 1, sm: 0 },
                                whiteSpace: "nowrap",
                                minWidth: "fit-content"
                            }}
                        >
                            Create New Question
                        </Button>
                        <Button
                            color="error"
                            variant="contained"
                            size="medium"
                            startIcon={<Delete />}
                            disabled={actionsDisabled}
                            onClick={handleBulkDelete}
                            sx={{
                                flex: { xs: 1, sm: 0 },
                                whiteSpace: "nowrap",
                                minWidth: "fit-content"
                            }}
                        >
                            Delete Selected ({selectedRows.length})
                        </Button>
                    </Stack>
                    {selectedSurveyUid && (
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ width: { xs: "100%", md: "auto" } }}>
                            <Button
                                variant="outlined"
                                size="medium"
                                startIcon={<Assessment />}
                                onClick={() => navigate(urls["adminSurveyAnalytics"].replace(":surveyUid", selectedSurveyUid))}
                                sx={{
                                    flex: { xs: 1, sm: 0 },
                                    whiteSpace: "nowrap",
                                    minWidth: "fit-content"
                                }}
                            >
                                View Analytics
                            </Button>
                            <Button
                                variant="outlined"
                                size="medium"
                                color="success"
                                startIcon={<ListAlt />}
                                onClick={() => navigate(urls["adminSurveyResponses"] + "?survey_uid=" + selectedSurveyUid)}
                                sx={{
                                    flex: { xs: 1, sm: 0 },
                                    whiteSpace: "nowrap",
                                    minWidth: "fit-content"
                                }}
                            >
                                View Responses
                            </Button>
                        </Stack>
                    )}
                </Box>

                {/* Data Table */}
                <Box sx={{ width: "100%", height: 600 }}>
                    <DataGrid
                        rows={pageData.questions}
                        columns={columns}
                        getRowId={(row) => row.uid}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 25,
                                },
                            },
                        }}
                        pageSizeOptions={[10, 25, 50, 100]}
                        checkboxSelection
                        disableRowSelectionOnClick
                        onRowSelectionModelChange={(rowSelectionModel) => {
                            setSelectedRows(rowSelectionModel as string[]);
                        }}
                        sx={{
                            "& .MuiDataGrid-cell": {
                                display: "flex",
                                alignItems: "center",
                            },
                        }}
                    />
                </Box>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={() => {
                        setDeleteDialogOpen(false);
                        setQuestionToDelete(null);
                    }}
                >
                    <DialogTitle>Confirm Delete</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to delete this survey question? This action will mark it as inactive.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => {
                                setDeleteDialogOpen(false);
                                setQuestionToDelete(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDeleteSingle}
                            color="error"
                            variant="contained"
                            disabled={deleteQuestionMutation.isPending}
                        >
                            {deleteQuestionMutation.isPending ? "Deleting..." : "Delete"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Stack>
        );
    }

    return null;
}
